import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, addWeeks } from "date-fns";
import { AlertTriangle, CalendarOff, ChevronLeft, ChevronRight, Plus, Truck, CheckCircle2 } from "lucide-react";
import { getCurrentWeekStart } from "../data/calendarData";
import { api } from "../api/client";
import { useFleetUnits, type FleetUnitRow } from "../screens/trucking/FleetRegistryScreen";
import { PageHeaderBand } from "../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../components/ui/LoadFailureNotice";
import { Tooltip } from "../components/ui/tooltip";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../lib/load-failure";
import { unwrap, unwrapArray } from "../lib/api-envelope";
import { loadStatusSkin } from "../lib/load-status";
import { useTenantTimeZone } from "../hooks/useTenantTimeZone";
import { calendarDayString, todayCalendarDate } from "../lib/calendar-date";
import { displayValue } from "../features/fieldiq-handoff/contracts";
import type { TruckingTimeOff } from "../types/trucking-time-off";

/**
 * Dispatch Calendar: the fleet's units down the side, the tenant's week across,
 * and each unit's driver's approved time off drawn on the days it covers.
 *
 * WHAT IT READ, AND WHAT IT READS NOW (sweep batch 10, #803).
 * - The rows came from `useTruckingData().trucks`, the legacy `trucks` table,
 *   which is empty for every tenant, so the grid only ever said "No trucks in
 *   fleet yet" beside a Fleet page listing eight units. The rows are now the
 *   fleet register's own list (GET /trucking/fleet, `trucking_fleet`).
 * - Time off never drew. GET /dispatch/time-off/calendar sends only APPROVED
 *   requests, with `first_name` / `last_name` and no `status`, while this page
 *   matched on `employee_name` / `driver_name` / `user_name` (never sent) by
 *   first-name substring, and coloured on `status` (never sent, so every block
 *   would have read "pending"). It then matched full names, which still put
 *   one "Sam Lee"'s time off on the other's unit.
 *
 * TIME OFF IS MATCHED BY ID, NEVER BY NAME (#815, api#1026). The page reads
 * GET /trucking/dispatch/time-off, trucking's own route: approved time off,
 * each row carrying the `trucking_driver_id` it belongs to, reached through
 * `trucking_drivers.user_id`. A block draws on a unit when that id is the
 * unit's `assigned_driver_id`. It is the same join the double-booking figure
 * uses, so a driver drawn as off is a driver the Clashes figure can flag.
 * (GET /dispatch/time-off/calendar is casing's /time-off Calendar tab and is
 * not read here.)
 *
 * A driver with no linked login cannot be matched, and that is SAID, not
 * left as an empty row: `unlinked_drivers` is the API's count of drivers on a
 * non-retired unit with no login, shown under the grid with the way to fix it
 * (a login is linked on the Drivers page). Absent reads "—", never 0.
 * - The three bottom figures (utilization, "fully booked today", "available")
 *   were derived from that empty `trucks` table, so they were structurally 0.
 *   The band now counts the fleet units and the time off in view, and
 *   "Available" is the API's `available_trucks` (GET /trucking/dispatch/metrics,
 *   api#860): active, unretired fleet units not on a dispatched, en-route or
 *   arrived load. It is the API's figure because only the API sees the loads
 *   against each unit; counting it here would be a second answer.
 * - GONE: the tab strip ("Active jobs" and "Drivers" both went to /trucking,
 *   and "Move jobs" changed nothing on the page), and the "pending" legend
 *   entry, which the route can never send.
 *
 * DOUBLE BOOKING (api#960). The band's third stat is the API's
 * `double_booking` for the days in view, as "trucks / drivers", under "Clashes": two loads on one
 * truck or one driver that overlap, or a driver on a load during approved time
 * off. It replaced "In Shop", because the band holds four stats and a unit's
 * shop status is already on the fleet register. The label is short on purpose:
 * this is the band's third column, whose label has a ~70px ceiling (CLAUDE.md).
 * "Double-booked" measured 116px and "Conflicts" 77px; "Clashes" (63px) over
 * "Trucks/drivers" swept equal to Quotes' third column, 1024-1440 every 8px. A row is marked when its unit
 * is in `truck_ids` or its assigned driver is in `driver_ids`; a double-booked
 * driver with no unit is in the count and on no row. `unplaceable_loads` (loads
 * with no departure time or duration, so the API cannot place them in time) is
 * a note beside the legend. Every one of these figures is the API's; the page
 * computes no overlap. Absent from the response reads "—", never 0.
 *
 * The legend keys only what the grid draws.
 *
 * LOADS DRAW ON THE GRID (fix/trucking-calendar-loads). Until then no cell had
 * ever drawn a load: ten loads in a week sat on Apex Hauling's register while
 * every cell read "+ Book". Each load is now a chip on its unit's row, on its
 * `scheduled_date`, from GET /trucking/loads (unpaginated, every row for the
 * company — the same read as Dispatch and the Overview widget):
 *   - placed on the row whose fleet id is the load's `truck_id`, AND on the row
 *     of its `trailer_id`, since a booked trailer is as committed as its truck;
 *   - a cancelled load is not drawn: it holds no unit on that day;
 *   - the chip's left rail and its accessible name carry the status, from the
 *     shared load-status palette (`lib/load-status.ts`), and it opens the BOL;
 *   - "+ Book" shows only on a day the unit has no load and no time off. A load
 *     on a day its driver is off draws beside the OFF marker, not instead of it.
 * This is placement, not arithmetic: no figure on the page is derived from it.
 * Loads with no truck and no trailer sit on no row; the note under the grid
 * names how many in the days shown, a tally of the unpaginated list (no
 * endpoint returns it), with Dispatch as the way to assign one.
 *
 * A FAILED LOADS READ MUST NOT LOOK LIKE AN EMPTY WEEK. It says so above the
 * grid, and the cells then offer no "+ Book" (which would assert the day is
 * free). While the read is in flight the cells are blank for the same reason.
 */

type ViewMode = "week" | "2weeks";

interface TimeOffRow {
  id: string;
  /** `trucking_drivers.id` — the only key a block is matched on. */
  driver_id: string | null;
  start_date: string | null;
  end_date: string | null;
  type: string | null;
  name: string | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const toTimeOff = (r: Record<string, unknown>): TimeOffRow => ({
  id: String(r.id ?? ""),
  driver_id: str(r.trucking_driver_id),
  start_date: calendarDayString(str(r.start_date)),
  end_date: calendarDayString(str(r.end_date)),
  type: str(r.time_off_type),
  name: [str(r.first_name), str(r.last_name)].filter(Boolean).join(" ") || null,
});

interface DoubleBooking {
  from?: string;
  to?: string;
  trucks?: number | null;
  drivers?: number | null;
  truck_ids?: string[];
  driver_ids?: string[];
  unplaceable_loads?: number | null;
}
interface DispatchCalendarMetrics {
  available_trucks?: number | null;
  double_booking?: DoubleBooking | null;
}
const isCount = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

const timeOffLabel = (t: string | null) => (t ? t.replace(/_/g, " ") : "");

interface CalendarLoad {
  id: string;
  bol_number: string | null;
  status: string | null;
  truck_id: string | null;
  trailer_id: string | null;
  /** YYYY-MM-DD, or null when the row has no usable date. */
  day: string | null;
  customer_name: string | null;
  /** The LOAD's driver (`loads.driver_id`), not the unit's usual one in the row header. */
  driver_id: string | null;
  driver_name: string | null;
}
const strOrNull = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);
const toCalendarLoad = (r: Record<string, unknown>): CalendarLoad => ({
  id: String(r.id ?? ""),
  bol_number: strOrNull(r.bol_number),
  status: strOrNull(r.status),
  truck_id: strOrNull(r.truck_id),
  trailer_id: strOrNull(r.trailer_id),
  day: calendarDayString(strOrNull(r.scheduled_date)),
  customer_name: strOrNull(r.customer_name),
  driver_id: strOrNull(r.driver_id),
  driver_name: [strOrNull(r.driver_first_name), strOrNull(r.driver_last_name)].filter(Boolean).join(" ") || null,
});
/** As the Dispatch board words it: no driver is "Unassigned"; a driver whose name did not arrive is a dash. */
const loadDriverLabel = (l: CalendarLoad) => (l.driver_id == null ? "Unassigned" : l.driver_name ?? "—");

const DispatchCalendar = () => {
  const navigate = useNavigate();
  // The board's week, as an offset from the TENANT's current week rather than a
  // stored Date: "this week" and "today" are the tenant's (companies.time_zone),
  // not the device's, and an offset keeps following them if the zone arrives
  // after the first render. Null only when the zone is unknown.
  const timeZone = useTenantTimeZone();
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const thisWeek = getCurrentWeekStart(timeZone);
  const weekStart = thisWeek ? addWeeks(thisWeek, weekOffset) : null;
  const todayStr = todayCalendarDate(timeZone);
  const isTenantToday = (d: Date) => todayStr !== null && format(d, "yyyy-MM-dd") === todayStr;

  const totalDays = viewMode === "week" ? 7 : 14;
  const days = !weekStart
    ? []
    : Array.from({ length: totalDays }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      });
  const dayStrs = days.map((d) => format(d, "yyyy-MM-dd"));
  const rangeFrom = dayStrs[0] ?? null;
  const rangeTo = dayStrs[dayStrs.length - 1] ?? null;

  const fleetQuery = useFleetUnits(false);
  const fleetFailed = queryLoadFailure(fleetQuery) != null;
  const fleetLoading = fleetQuery.isLoading && !fleetFailed;
  const units: FleetUnitRow[] | undefined = fleetFailed ? undefined : fleetQuery.data;

  // The period is the days in view, so this key carries the range and is not
  // the dashboard's. `available_trucks` is a now-figure the period does not move.
  const metricsQuery = useQuery({
    queryKey: ["trucking-dispatch-metrics", rangeFrom, rangeTo],
    queryFn: () =>
      api.trucking.dispatch
        .metrics({ from: rangeFrom as string, to: rangeTo as string })
        .then((r) => unwrap<DispatchCalendarMetrics>(r.data)),
    enabled: rangeFrom !== null && rangeTo !== null,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const metricsFailed = queryLoadFailure(metricsQuery) != null;
  const metricsLoading = (metricsQuery.isLoading || !rangeFrom) && !metricsFailed;
  const booking = metricsFailed ? null : metricsQuery.data?.double_booking ?? null;
  const conflictTrucks = new Set(booking?.truck_ids ?? []);
  const conflictDrivers = new Set(booking?.driver_ids ?? []);
  const isDoubleBooked = (u: FleetUnitRow) => conflictTrucks.has(u.id) || (u.driver_id != null && conflictDrivers.has(u.driver_id));
  const conflictsFig =
    booking && isCount(booking.trucks) && isCount(booking.drivers) ? `${booking.trucks} / ${booking.drivers}` : "—";
  const unplaceable = booking && isCount(booking.unplaceable_loads) ? booking.unplaceable_loads : null;

  // Every load for the company (the route is unpaginated); the grid places them.
  const loadsQuery = useQuery({
    queryKey: ["trucking-loads", "calendar"],
    queryFn: () => api.trucking.loads.list().then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toCalendarLoad)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const loadsFailed = queryLoadFailure(loadsQuery) != null;
  const loadsLoading = loadsQuery.isLoading && !loadsFailed;
  // Cancelled loads hold no unit on their day, so they are not drawn.
  const liveLoads: CalendarLoad[] = loadsFailed ? [] : (loadsQuery.data ?? []).filter((l) => l.status !== "cancelled");
  const loadsFor = (unit: FleetUnitRow, day: string) =>
    liveLoads.filter((l) => l.day === day && (l.truck_id === unit.id || l.trailer_id === unit.id));
  const unitIds = new Set((units ?? []).map((u) => u.id));
  const unplacedInView =
    loadsFailed || loadsLoading || !units
      ? null
      : liveLoads.filter(
          (l) => l.day != null && dayStrs.includes(l.day) && !(l.truck_id && unitIds.has(l.truck_id)) && !(l.trailer_id && unitIds.has(l.trailer_id)),
        ).length;

  const timeOffQuery = useQuery({
    queryKey: ["trucking-dispatch-time-off", rangeFrom, rangeTo],
    queryFn: () =>
      api.trucking.dispatch.timeOff(rangeFrom as string, rangeTo as string).then((r) => {
        // Partial: the declared shape is what the route sends, and a field that
        // did not arrive must still read as absent here, never as 0.
        const body = unwrap<Partial<TruckingTimeOff>>(r.data);
        return {
          rows: (body?.rows ?? []).map((row) => toTimeOff(row as unknown as Record<string, unknown>)),
          unlinked: body && isCount(body.unlinked_drivers) ? body.unlinked_drivers : null,
        };
      }),
    enabled: rangeFrom !== null && rangeTo !== null,
    staleTime: 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const timeOffFailed = queryLoadFailure(timeOffQuery) != null;
  const timeOffLoading = (timeOffQuery.isLoading || !rangeFrom) && !timeOffFailed;
  const timeOff: TimeOffRow[] | undefined = timeOffFailed ? undefined : timeOffQuery.data?.rows;
  const unlinkedDrivers = timeOffFailed ? null : timeOffQuery.data?.unlinked ?? null;

  // By id only: a unit with no assigned driver, or a row with no driver id, matches nothing.
  const timeOffFor = (unit: FleetUnitRow, day: string) =>
    unit.driver_id == null
      ? null
      : (timeOff ?? []).find((t) => t.driver_id === unit.driver_id && t.start_date != null && t.end_date != null && t.start_date <= day && t.end_date >= day) ?? null;

  /** Tallies of the loaded lists: GET /trucking/fleet is unpaginated, and the time-off window is this view. */
  const unitCount = (match: (u: FleetUnitRow) => boolean) => (units ? units.filter(match).length : null);
  const fleetFig = (n: number | null) => (fleetFailed ? "—" : displayValue(n));

  const gridCols = viewMode === "week" ? "grid-cols-[120px_repeat(7,minmax(0,1fr))]" : "grid-cols-[120px_repeat(14,minmax(0,1fr))]";
  const rangeLabel = days.length ? `${format(days[0], "MMM d")} – ${format(days[days.length - 1], "MMM d, yyyy")}` : "—";

  const pill = (active: boolean) =>
    `inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
      active ? "bg-fi-amber text-fi-amber-ink" : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
    }`;
  const iconBtn =
    "inline-flex size-8 items-center justify-center rounded-fi-control border border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/50";

  return (
    <div data-testid="dispatch-calendar-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking"
        title="Dispatch Calendar"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate("/trucking/dispatch")}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              List view
            </button>
            <button type="button" onClick={() => navigate("/trucking/new-load")} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Book Load
            </button>
          </>
        }
        stats={[
          { icon: <Truck className="size-4" aria-hidden="true" />, label: "Units", sub: "In the fleet", tone: "neutral", loading: fleetLoading, value: fleetFig(unitCount(() => true)) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: "Available", sub: "Not on a load", tone: "success", loading: metricsLoading, value: metricsFailed ? "—" : displayValue(metricsQuery.data?.available_trucks) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: "Clashes", sub: "Trucks/drivers", tone: "accent", loading: metricsLoading, value: conflictsFig },
          { icon: <CalendarOff className="size-4" aria-hidden="true" />, label: "Time Off", sub: "In this view", tone: "info", loading: timeOffLoading, value: timeOffFailed ? "—" : displayValue(timeOff ? timeOff.length : null) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <button type="button" aria-label="Previous week" className={iconBtn} onClick={() => setWeekOffset((o) => o - 1)}>
                <ChevronLeft className="size-4" aria-hidden="true" />
              </button>
              <span data-testid="calendar-range" className="min-w-[180px] text-center text-sm font-bold text-fieldiq-text">{rangeLabel}</span>
              <button type="button" aria-label="Next week" className={iconBtn} onClick={() => setWeekOffset((o) => o + 1)}>
                <ChevronRight className="size-4" aria-hidden="true" />
              </button>
              <button type="button" className={pill(false)} onClick={() => setWeekOffset(0)}>
                Today
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 text-xs text-fieldiq-muted">
                <span className="size-3 rounded-sm border border-black/20 bg-fi-signal-red" aria-hidden="true" />
                Time off (approved)
              </span>
              <span className="flex items-center gap-1.5 text-xs text-fieldiq-muted">
                <span className="size-3 rounded-sm border border-black/20 bg-fi-signal-amber" aria-hidden="true" />
                Double-booked
              </span>
              {unplaceable != null && unplaceable > 0 && (
                <span data-testid="unplaceable-note" className="text-xs font-bold text-fieldiq-text">
                  {unplaceable === 1 ? "1 load has no time" : `${unplaceable} loads have no time`}
                </span>
              )}
              <div className="flex gap-2" role="group" aria-label="Range">
                <button type="button" aria-pressed={viewMode === "week"} className={pill(viewMode === "week")} onClick={() => setViewMode("week")}>
                  Week
                </button>
                <button type="button" aria-pressed={viewMode === "2weeks"} className={pill(viewMode === "2weeks")} onClick={() => setViewMode("2weeks")}>
                  2 weeks
                </button>
              </div>
            </div>
          </div>

          <section
            aria-labelledby="calendar-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Fleet by day</p>
              <h2 id="calendar-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                {rangeLabel}
              </h2>
            </header>

            {/* Loads failing to load must not read as an empty week. */}
            {loadsFailed && (
              <div className="border-b border-fieldiq-border">
                <LoadFailureNotice error={queryFailureError(loadsQuery)} subject="the loads" />
              </div>
            )}

            {/* Time off failing to load must not read as nobody being off. */}
            {timeOffFailed && (
              <div className="border-b border-fieldiq-border">
                <LoadFailureNotice error={queryFailureError(timeOffQuery)} subject="time off" allowed="admins, office staff, supervisors, dispatchers and logistics" />
              </div>
            )}

            {fleetFailed ? (
              <LoadFailureNotice error={queryFailureError(fleetQuery)} subject="the fleet" />
            ) : fleetLoading ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : (units ?? []).length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">No units in the fleet yet</p>
            ) : (
              <div className="overflow-x-auto">
                <div className={viewMode === "week" ? "min-w-[760px]" : "min-w-[1200px]"} role="table" aria-label="Fleet by day">
                  <div role="row" className={`grid ${gridCols} border-b border-fieldiq-border bg-fieldiq-dark/40`}>
                    <div role="columnheader" className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Unit</div>
                    {days.map((d, i) => (
                      <div
                        key={i}
                        role="columnheader"
                        aria-current={isTenantToday(d) ? "date" : undefined}
                        className={`border-l border-fieldiq-border px-1 py-2 text-center ${isTenantToday(d) ? "bg-fi-amber/10" : ""}`}
                      >
                        <p className="text-[10px] font-bold uppercase text-fieldiq-muted">{format(d, "EEE")}</p>
                        <p className="text-base font-extrabold text-fieldiq-text">{format(d, "d")}</p>
                      </div>
                    ))}
                  </div>

                  {(units ?? []).map((unit) => {
                    const doubleBooked = isDoubleBooked(unit);
                    return (
                    <div
                      key={unit.id}
                      role="row"
                      data-testid="calendar-unit-row"
                      data-double-booked={doubleBooked ? "true" : undefined}
                      className={`grid ${gridCols} border-b border-fieldiq-border last:border-b-0`}
                    >
                      <div role="rowheader" className="px-3 py-3">
                        <p className="font-mono text-sm font-bold text-fieldiq-text">{unit.unit_number ?? "—"}</p>
                        <p className="truncate text-[11px] text-fieldiq-muted">{unit.driver ?? "No driver"}</p>
                        {doubleBooked && (
                          <span
                            data-testid="double-booked-badge"
                            className="mt-1 inline-block rounded-sm border border-black/20 bg-fi-signal-amber px-1 py-0.5 text-[9px] font-black uppercase leading-tight text-fi-signal-ink"
                          >
                            Double-booked
                          </span>
                        )}
                      </div>
                      {dayStrs.map((day, i) => {
                        const off = timeOffFor(unit, day);
                        const dayLoads = loadsFor(unit, day);
                        // "+ Book" claims the day is free, so only once the loads have arrived.
                        const canBook = !off && dayLoads.length === 0 && !loadsLoading && !loadsFailed;
                        return (
                          <div
                            key={day}
                            role="cell"
                            className={`flex min-h-[52px] flex-col items-stretch justify-center gap-1 border-l border-fieldiq-border px-0.5 py-1.5 ${isTenantToday(days[i]) ? "bg-fi-amber/5" : ""}`}
                          >
                            {off && (
                              <div
                                data-testid="time-off-block"
                                className="w-full rounded-sm border border-black/20 bg-fi-signal-red px-1 py-1 text-center text-fi-signal-ink"
                                title={`${off.name ?? "—"}: time off${off.type ? ` (${timeOffLabel(off.type)})` : ""}`}
                              >
                                <p className="text-[10px] font-black leading-tight">OFF</p>
                                {off.type && <p className="truncate text-[9px] leading-tight">{timeOffLabel(off.type)}</p>}
                              </div>
                            )}
                            {dayLoads.map((load) => {
                              const skin = loadStatusSkin(load.status);
                              const bol = load.bol_number ?? "—";
                              const driver = loadDriverLabel(load);
                              return (
                                <Tooltip key={load.id} content={`BOL ${bol} · ${load.customer_name ?? "—"} · ${driver} · ${skin.label}`} className="flex w-full">
                                <button
                                  type="button"
                                  data-testid="calendar-load"
                                  onClick={() => navigate(`/trucking/bols/${load.id}`)}
                                  aria-label={`BOL ${bol}, ${load.customer_name ?? "no customer"}, driver ${driver}, ${skin.label}`}
                                  className={`w-full rounded-sm border border-fieldiq-border border-l-4 ${skin.rail} bg-fieldiq-card px-1 py-0.5 text-left hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 focus-visible:outline-none`}
                                >
                                  <p className="truncate font-mono text-[10px] font-bold leading-tight text-fieldiq-text">{bol}</p>
                                  <p className="truncate text-[9px] leading-tight text-fieldiq-muted">{load.customer_name ?? "—"}</p>
                                  <p data-testid="calendar-load-driver" className="truncate text-[9px] leading-tight text-fieldiq-muted">{driver}</p>
                                </button>
                                </Tooltip>
                              );
                            })}
                            {canBook && (
                              <button
                                type="button"
                                onClick={() => navigate("/trucking/new-load")}
                                aria-label={`Book a load for ${unit.unit_number ?? "this unit"} on ${format(days[i], "MMM d")}`}
                                className="self-center rounded px-1.5 py-1 text-[10px] text-fieldiq-muted hover:bg-fieldiq-dark/50 hover:text-fieldiq-text"
                              >
                                + Book
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Scope: loads in these days that sit on no row, and where to give them a unit. */}
            {!fleetFailed && !fleetLoading && unplacedInView != null && unplacedInView > 0 && (
              <p data-testid="unplaced-loads-note" className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                <span className="font-bold text-fieldiq-text">
                  {unplacedInView === 1 ? "1 load in these days has no unit" : `${unplacedInView} loads in these days have no unit`}
                </span>
                {unplacedInView === 1 ? ", so it isn\u2019t on a row." : ", so they aren\u2019t on a row."}{" "}
                <Link to="/trucking/dispatch" className="font-bold text-fieldiq-text underline underline-offset-2">
                  Assign one on Dispatch
                </Link>
              </p>
            )}
            {/* Scope, not internals: which drivers' time off this grid cannot show, and where to fix it. */}
            {!fleetFailed && !fleetLoading && !timeOffFailed && !timeOffLoading && (units ?? []).length > 0 && unlinkedDrivers !== 0 && (
              <p data-testid="unlinked-drivers-note" className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                {unlinkedDrivers == null ? (
                  <>Drivers with no login linked: &mdash;</>
                ) : (
                  <>
                    <span className="font-bold text-fieldiq-text">
                      {unlinkedDrivers === 1 ? "1 driver has no login linked" : `${unlinkedDrivers} drivers have no login linked`}
                    </span>
                    , so their time off can&rsquo;t show here.{" "}
                    <Link to="/trucking/drivers" className="font-bold text-fieldiq-text underline underline-offset-2">
                      Link a login on Drivers
                    </Link>
                  </>
                )}
              </p>
            )}
          </section>
        </div>
      </section>
    </div>
  );
};

export default DispatchCalendar;
